import { Link } from "react-router-dom";
import { ShieldCheck, Award, Hammer, Users, Star, ArrowRight, Phone } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import LastUpdated from "../components/LastUpdated";
import { COMPANY, IMAGES, PAGE_UPDATED, webPageLd, breadcrumbLd } from "../data/site";

/* eslint-disable react/no-unescaped-entities */

const values = [
  { icon: ShieldCheck, title: "Bonded & Insured", desc: "Fully insured in Texas. Certificate of insurance on every job." },
  { icon: Hammer, title: "We self-perform", desc: "Concrete, masonry and landscape — no subcontractor mystery." },
  { icon: Award, title: "Warranty in writing", desc: "Two-year warranty on all flatwork. We stand behind our pours." },
  { icon: Users, title: "Local Austin team", desc: "Born and built in Austin. Our crews live here too." },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About Us — Austin TX Contractor | Texan Strong Builds"
        description="Texan Strong Builds is a bonded, insured concrete & landscape contractor in Austin, TX with 10+ years of experience and a 2-year warranty on flatwork."
        canonical={`${window.location.origin}/about`}
        jsonLd={[
          webPageLd({
            name: "About Texan Strong Builds | Austin TX Concrete & Landscape Contractor",
            path: "/about",
            updated: PAGE_UPDATED.about,
            description: "Texan Strong Builds is a bonded and insured Austin, TX concrete and landscape contractor with 10+ years of experience.",
            type: "AboutPage",
          }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />

      <section className="bg-stone-900 text-white pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Breadcrumbs dark items={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]} />
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-red-500">About TSB</div>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Built in Austin.<br />Built to last.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-stone-300 text-lg leading-relaxed">
              Texan Strong Builds is a family-rooted Austin, TX contractor specializing in concrete and
              landscape work. Texan Strong Builds started pouring driveways for Austin neighbors a decade
              ago; today its crews handle full residential and commercial projects across the entire
              Austin metro.
            </p>
            <LastUpdated dark date={PAGE_UPDATED.about} className="mt-5" />
          </div>
        </div>
      </section>

      <section className="bg-stone-50 py-20 md:py-28 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <img
              src={IMAGES.concreteWorker}
              alt="Texan Strong Builds crew pouring concrete in Austin"
              className="w-full aspect-[4/3] object-cover border border-stone-200"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-red-600">Our story</div>
            <h2 className="mt-3 font-display text-4xl font-black tracking-tighter text-stone-900 leading-tight">
              Concrete should outlast the people who pour it.
            </h2>
            <div className="mt-6 space-y-5 text-stone-700 text-lg leading-relaxed">
              <p>
                The Texas building boom flooded Austin with shortcut contractors: hairline cracks
                in two seasons, driveways tilting in three, patios pulling away from foundations.
              </p>
              <p>
                Texan Strong Builds was built to fix that. Texan Strong Builds pours 5" reinforced
                slabs as a default, cuts control joints on time, and uses rebar on chairs — not mesh
                on dirt. Every Austin project gets pulled permits and a written warranty, because
                cheap concrete is the most expensive concrete a homeowner can buy.
              </p>
              <p>
                On the landscape side, Texan Strong Builds leads with <strong>xeriscape</strong>.
                Austin's water future demands drought-tolerant design, and customers love both the
                lower water bills and the look.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-red-600">What we promise</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tighter text-stone-900 leading-[1.02]">
            How TSB works.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="border border-stone-200 p-7" data-testid={`value-${title.toLowerCase().replace(/\s/g, "-")}`}>
                <Icon size={22} className="text-red-600" strokeWidth={2.2} />
                <h3 className="mt-4 font-display text-lg font-bold text-stone-900">{title}</h3>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-stone-900 text-white py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <Star size={28} className="text-yellow-400 mx-auto" fill="#facc15" />
          <h3 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[1.02]">
            See what neighbors are saying.
          </h3>
          <p className="mt-4 text-stone-300 text-lg">
            5-star reviewed across Austin and the Hill Country.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <a href={COMPANY.gmb} target="_blank" rel="noopener noreferrer" className="btn-ghost-dark">
              View Google reviews
            </a>
            <Link to="/contact" className="btn-primary">
              Get a Free Estimate <ArrowRight size={18} />
            </Link>
          </div>
          <div className="mt-6 flex justify-center">
            <a href={COMPANY.phoneTel} className="inline-flex items-center gap-2 text-sm text-stone-400 hover:text-white">
              <Phone size={14} /> Or call {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
